import { Model } from '@/models/Model';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  AddCommand,
  CacheCommandI,
  MergeCommand,
  RemoveCommand,
  SetCommand,
  UpdateCommand,
} from './CacheCommand';
import {
  AddOptions,
  CacheStrategy,
  UpdateItem,
  UpdateOptions,
} from './strategy/CacheStrategy';
import CacheStrategyFactory, {
  CacheType,
} from './strategy/CacheStrategyFactory';
import { ListCacheStrategy } from './strategy/list/BaseListCacheStrategy';
import { InfiniteCacheStrategy } from './strategy/list/InfiniteCacheStrategy';

export interface CacheManagerOptions {
  queryKey: QueryKey;
  cacheType: CacheType;
  queryClient: QueryClient;
}

export type CacheRollback = () => void;

export class CacheManager<T extends Model> {
  protected strategy: CacheStrategy<T>;
  protected commandHistory: CacheCommandI<T>[] = [];

  constructor(options: CacheManagerOptions) {
    const queryClient = options.queryClient;
    this.strategy = CacheStrategyFactory.create<T>(
      options.cacheType,
      queryClient,
      options.queryKey,
    );
  }

  get(): T | T[] | undefined {
    return this.strategy.get();
  }

  protected run(command: CacheCommandI<T>): CacheRollback {
    command.execute();
    this.commandHistory.push(command);

    return () => command.undo();
  }

  remove(id: number): CacheRollback {
    return this.run(new RemoveCommand(this.strategy, id));
  }

  set(data: T | T[]): CacheRollback {
    return this.run(new SetCommand(this.strategy, data));
  }

  setIfAbsent(data: T | T[]): void {
    this.strategy.setIfAbsent(data);
  }

  merge(data: Partial<T>): CacheRollback {
    return this.run(new MergeCommand(this.strategy, data));
  }

  undo(): void {
    this.commandHistory.pop()?.undo();
  }

  batch(operations: (() => void)[]): void {
    operations.forEach(operation => operation());
  }
}

export class ListCacheManager<T extends Model> extends CacheManager<T> {
  protected declare strategy: ListCacheStrategy<T>;

  constructor(options: CacheManagerOptions) {
    super(options);
  }

  get(): T[] | undefined {
    return this.strategy.get();
  }

  getById(id: number): T | undefined {
    return this.strategy.getById(id);
  }

  setIfAbsent(data: T[]): void {
    this.strategy.setIfAbsent(data);
  }

  add(data: T, options?: AddOptions): CacheRollback {
    return this.run(new AddCommand(this.strategy, data, options));
  }

  update(data: UpdateItem<T>, options?: UpdateOptions): CacheRollback {
    return this.run(new UpdateCommand(this.strategy, data, options));
  }
}

export class InfiniteCacheManager<T extends Model> extends ListCacheManager<T> {
  protected declare strategy: InfiniteCacheStrategy<T>;

  constructor(options: CacheManagerOptions) {
    super(options);
  }
}
